/**
 * Normalize the small contract drift seen between persisted Inbox messages
 * and provider/webhook payloads. The UI consumes only the canonical
 * `media_*` fields; this adapter lets the backend place the same attachment
 * under `media`, `attachment(s)`, or `metadata` while ingestion is converging.
 *
 * Deliberately shallow: arbitrary URLs elsewhere in metadata (avatars,
 * quoted-message links, tracking data) must never turn a text message into an
 * attachment by accident.
 */

type UnknownRecord = Record<string, unknown>;

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document', 'sticker']);

function record(value: unknown): UnknownRecord | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;
}

function text(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function firstRecord(value: unknown): UnknownRecord | undefined {
  if (!Array.isArray(value)) return record(value);
  return value.map(record).find((item): item is UnknownRecord => !!item);
}

function looksLikeMessage(value: UnknownRecord): boolean {
  return [
    'id',
    'message_id',
    'content',
    'message_type',
    'media_url',
    'media',
    'attachment',
    'attachments',
  ].some((key) => key in value);
}

/** Unwrap common `{ message }` / `{ data: { message } }` event envelopes. */
export function extractInboxMessagePayload(value: unknown): UnknownRecord {
  const outer = record(value) ?? {};
  const data = record(outer.data);
  const nested = record(outer.message) ?? record(data?.message);

  if (nested) return { ...outer, ...data, ...nested };
  if (data && looksLikeMessage(data)) return { ...outer, ...data };
  return outer;
}

interface MediaCandidate {
  value: UnknownRecord;
  typeHint?: string;
}

function mediaCandidates(message: UnknownRecord): MediaCandidate[] {
  const metadata = record(message.metadata);
  const candidates: Array<MediaCandidate | undefined> = [
    { value: message },
    record(message.image) ? { value: record(message.image)!, typeHint: 'image' } : undefined,
    record(message.video) ? { value: record(message.video)!, typeHint: 'video' } : undefined,
    record(message.audio) ? { value: record(message.audio)!, typeHint: 'audio' } : undefined,
    record(message.document)
      ? { value: record(message.document)!, typeHint: 'document' }
      : undefined,
    record(message.media) ? { value: record(message.media)! } : undefined,
    firstRecord(message.attachment) ? { value: firstRecord(message.attachment)! } : undefined,
    firstRecord(message.attachments) ? { value: firstRecord(message.attachments)! } : undefined,
    metadata ? { value: metadata } : undefined,
    record(metadata?.media) ? { value: record(metadata?.media)! } : undefined,
    firstRecord(metadata?.attachment)
      ? { value: firstRecord(metadata?.attachment)! }
      : undefined,
    firstRecord(metadata?.attachments)
      ? { value: firstRecord(metadata?.attachments)! }
      : undefined,
    record(metadata?.image) ? { value: record(metadata?.image)!, typeHint: 'image' } : undefined,
    record(metadata?.video) ? { value: record(metadata?.video)!, typeHint: 'video' } : undefined,
    record(metadata?.audio) ? { value: record(metadata?.audio)!, typeHint: 'audio' } : undefined,
    record(metadata?.document)
      ? { value: record(metadata?.document)!, typeHint: 'document' }
      : undefined,
  ];
  return candidates.filter((item): item is MediaCandidate => !!item);
}

function candidateUrl(candidate: UnknownRecord): string | undefined {
  const payload = record(candidate.payload);
  return text(
    candidate.media_url,
    candidate.url,
    candidate.file_url,
    candidate.download_url,
    candidate.link,
    candidate.mediaUrl,
    candidate.fileUrl,
    payload?.media_url,
    payload?.url,
    payload?.file_url,
  );
}

function candidateMime(candidate: UnknownRecord): string | undefined {
  return text(
    candidate.media_mime_type,
    candidate.mime_type,
    candidate.mimetype,
    candidate.content_type,
    candidate.mime,
    candidate.mimeType,
  );
}

function candidateFileName(candidate: UnknownRecord): string | undefined {
  return text(
    candidate.media_file_name,
    candidate.file_name,
    candidate.filename,
    candidate.name,
    candidate.fileName,
  );
}

function candidateCaption(candidate: UnknownRecord): string | undefined {
  return text(candidate.media_caption, candidate.caption);
}

function inferType(
  candidate: MediaCandidate,
  mime: string | undefined,
  fileName: string | undefined,
): string | undefined {
  const declared = text(
    candidate.typeHint,
    candidate.value.message_type,
    candidate.value.media_type,
    candidate.value.type,
  )?.toLowerCase();
  if (declared && MEDIA_TYPES.has(declared)) return declared;

  const lowerMime = mime?.toLowerCase();
  if (lowerMime?.startsWith('image/')) return 'image';
  if (lowerMime?.startsWith('video/')) return 'video';
  if (lowerMime?.startsWith('audio/')) return 'audio';
  if (lowerMime) return 'document';

  const extension = fileName?.match(/\.([a-z0-9]{1,8})(?:[?#].*)?$/i)?.[1]?.toLowerCase();
  if (extension && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'heic'].includes(extension)) {
    return 'image';
  }
  if (extension && ['mp4', 'mov', 'webm', 'm4v'].includes(extension)) return 'video';
  if (extension && ['mp3', 'm4a', 'ogg', 'opus', 'wav', 'aac'].includes(extension)) return 'audio';
  if (extension) return 'document';
  return undefined;
}

/**
 * Return a message with canonical media fields. Existing canonical values win;
 * `content` is never rewritten, so captions and ordinary text keep their
 * original semantics.
 */
export function normalizeInboxMessage(value: unknown): UnknownRecord {
  const message = extractInboxMessagePayload(value);
  const candidates = mediaCandidates(message);
  const withUrl = candidates.find((candidate) => !!candidateUrl(candidate.value));
  if (!withUrl) return message;

  const mediaUrl = text(message.media_url) ?? candidateUrl(withUrl.value);
  const mediaMimeType =
    text(message.media_mime_type) ??
    candidates.map((candidate) => candidateMime(candidate.value)).find(Boolean);
  const mediaFileName =
    text(message.media_file_name) ??
    candidates.map((candidate) => candidateFileName(candidate.value)).find(Boolean);
  const mediaCaption =
    text(message.media_caption) ??
    candidates.map((candidate) => candidateCaption(candidate.value)).find(Boolean);
  const explicitType = text(message.message_type)?.toLowerCase();
  const inferredType = candidates
    .map((candidate) =>
      inferType(
        candidate,
        candidateMime(candidate.value) ?? mediaMimeType,
        candidateFileName(candidate.value) ?? mediaFileName,
      ),
    )
    .find(Boolean);
  const messageType =
    explicitType && explicitType !== 'text'
      ? explicitType
      : inferredType ?? 'document';

  return {
    ...message,
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
    ...(mediaMimeType ? { media_mime_type: mediaMimeType } : {}),
    ...(mediaFileName ? { media_file_name: mediaFileName } : {}),
    ...(mediaCaption ? { media_caption: mediaCaption } : {}),
    message_type: messageType,
  };
}
